/**
 * The filters HR asked for (9 Oct 2026): function, department, Team 1 to 3,
 * leadership layer, grade, role and critical role. They apply to every view and
 * every download. Each list offers only what the other filters leave, so a
 * choice never leads to an empty screen.
 */
import React from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  EMPTY_FILTERS,
  FILTER_FIELDS,
  activeFilterCount,
  type FilterKey,
  type TalentFilters,
} from './talentDashboardData'

const ALL = '__all__'

export const TalentFilterBar: React.FC<{
  filters: TalentFilters
  options: Record<FilterKey, string[]>
  onChange: (next: TalentFilters) => void
}> = ({ filters, options, onChange }) => {
  const active = activeFilterCount(filters)
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3" aria-label="Filters">
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        {FILTER_FIELDS.map(({ key, label }) => {
          const choices = options[key] || []
          // Keep a chosen value listed even when the other filters now exclude it.
          const listed = filters[key] && !choices.includes(filters[key]) ? [filters[key], ...choices] : choices
          return (
            <label key={key} className="min-w-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              {label}
              <Select
                value={filters[key] || ALL}
                onValueChange={(v) => onChange({ ...filters, [key]: v === ALL ? '' : v })}
              >
                <SelectTrigger className="mt-1 h-8 w-full text-xs font-normal normal-case tracking-normal text-gray-800" aria-label={label}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All</SelectItem>
                  {listed.map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          )
        })}
      </div>
      {active ? (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-500">
          {active} {active === 1 ? 'filter' : 'filters'} applied to every view and download.
          <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" onClick={() => onChange(EMPTY_FILTERS)}>
            <X className="h-3 w-3" />
            Clear filters
          </Button>
        </div>
      ) : null}
    </div>
  )
}
